// ─── «Ver portal»: el personal mira el portal de un alumno, en solo lectura ─
// Lo que decide el navegador (puro, con test). El servidor decide el resto: la
// vista la abre `openPortalView` —y queda en la auditoría— y cada lectura la
// valida (functions/src/lib/portal-view.ts). Acá:
//   · qué rutas del portal existen en la vista (la versión 1);
//   · que TODA llamada lleve el `portalViewId` mientras se mira el portal, y
//     ninguna fuera de él;
//   · cuándo la vista venció.

export interface PortalView {
  viewId: string;
  /** Quien la abrió. La vista existe SOLO para esa sesión: con otro usuario
   *  —cerró sesión y entró otra persona en la misma pestaña—, no hay vista. */
  viewerUid: string;
  tenantId: string;
  personId: string;
  studentCode: string | null;
  /** ISO. */
  expiresAt: string;
}

const PORTAL = '/portal-v2';

/** Las páginas de la versión 1: inicio, cursos (con sus pestañas de lectura),
 *  Historial/expediente y calendario. Lo demás —inscribirse, entregar,
 *  encuestas, diario, oración, mentoría, pagos, becas…— no existe en la vista. */
const VIEW_PATHS: RegExp[] = [
  /^\/portal-v2\/?$/,
  /^\/portal-v2\/courses\/?$/,
  /^\/portal-v2\/courses\/[^/]+\/?$/,
  /^\/portal-v2\/courses\/[^/]+\/(modules|clases|assignments|grades|syllabus)\/?$/,
  /^\/portal-v2\/historial(\/transcript)?\/?$/,
  /^\/portal-v2\/calendar\/?$/,
];

export function isPortalViewPath(pathname: string): boolean {
  return VIEW_PATHS.some((re) => re.test(pathname));
}

/** La vista, solo si es de quien tiene la sesión. Sin sesión, o con otra, null:
 *  nada de su código, su persona ni su portalViewId llega a otra cuenta. */
export function viewFor(
  view: PortalView | null,
  uid: string | null | undefined,
): PortalView | null {
  return view && uid && view.viewerUid === uid ? view : null;
}

export function isInPortal(pathname: string): boolean {
  return pathname === PORTAL || pathname.startsWith(`${PORTAL}/`);
}

/** El payload de una llamada, con el `portalViewId` si hay una vista y la
 *  pantalla es del portal. Fuera del portal NO se agrega: el personal que
 *  vuelve a su panel no puede quedar con las escrituras bloqueadas. */
export function withPortalView<T>(
  payload: T,
  view: PortalView | null,
  pathname: string,
): T | (T & { portalViewId: string }) {
  if (!view || !isInPortal(pathname)) return payload;
  if (payload !== null && payload !== undefined && typeof payload !== 'object') return payload;
  return { ...((payload ?? {}) as object), portalViewId: view.viewId } as T & {
    portalViewId: string;
  };
}

export function isPortalViewExpired(view: PortalView, now: Date = new Date()): boolean {
  return new Date(view.expiresAt).getTime() <= now.getTime();
}

/** Por qué la vista ya no sirve, según el servidor. */
export type PortalViewGone = 'expired' | 'invalid' | 'deceased';

/** Los motivos del servidor que CIERRAN la vista: vencida, inválida, o la
 *  persona pasó a figurar como fallecida en el origen después de abrirla. */
const GONE_BY_REASON: Record<string, PortalViewGone> = {
  'portal_view.expired': 'expired',
  'portal_view.invalid': 'invalid',
  'person.deceased_in_source': 'deceased',
};

/** El motivo de un error, si es uno de los que cierran la vista. */
export function portalViewGoneOf(error: unknown): PortalViewGone | null {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  return typeof reason === 'string' ? (GONE_BY_REASON[reason] ?? null) : null;
}
